"use client";

import { useState } from "react";
import { 
  Briefcase, 
  Building2, 
  UserCheck, 
  Award, 
  ArrowRight, 
  Calendar,
  FileText,
  MapPin,
  ChevronRight
} from "lucide-react";
import { CandidateLead } from "@/lib/demos/hiringDb";
import { HIRING_STAGES } from "@/lib/demos/hiringData";

interface HiringKanbanProps {
  candidates: CandidateLead[];
  onSelectCandidate: (candidate: CandidateLead) => void;
  onRequestStageAdvance: (candidate: CandidateLead, targetStage: string) => void;
  onDirectStageMove: (candidateId: number, targetStage: string) => Promise<void>;
  currency: "INR" | "USD";
}

export function HiringKanban({
  candidates,
  onSelectCandidate,
  onRequestStageAdvance,
  onDirectStageMove,
  currency,
}: HiringKanbanProps) {
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const sym = currency === "USD" ? "$" : "₹";

  function handleDragStart(e: React.DragEvent, id: number) {
    e.dataTransfer.setData("text/plain", String(id));
    setDraggedId(id);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  async function handleDrop(e: React.DragEvent, targetStage: string) {
    e.preventDefault();
    const idStr = e.dataTransfer.getData("text/plain");
    const candidateId = Number(idStr);
    setDraggedId(null);

    if (!candidateId) return;
    const candidate = candidates.find((c) => c.id === candidateId);
    if (!candidate || candidate.stage === targetStage) return;

    // If moving to Client Interview Round or Placed (Won), open modal for required details
    if (targetStage === "Client Interview Round" || targetStage === "Offer Released & Placed (Won)") {
      onRequestStageAdvance(candidate, targetStage);
    } else {
      await onDirectStageMove(candidateId, targetStage);
    }
  }

  return (
    <div className="w-full overflow-x-auto pb-6">
      <div className="flex gap-4 min-w-[1350px]">
        {HIRING_STAGES.map((stage) => {
          const stageCandidates = candidates.filter((c) => c.stage === stage.name);

          return (
            <div
              key={stage.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.name)}
              className="flex-1 min-w-[220px] max-w-[280px] bg-[#120a1f]/80 rounded-2xl border border-purple-900/40 flex flex-col h-[750px] shadow-lg shadow-purple-950/40"
            >
              {/* Column Header */}
              <div className="p-3.5 border-b border-purple-900/40 flex items-center justify-between bg-[#170e28]">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      stage.id === "placed"
                        ? "bg-emerald-400 ring-2 ring-emerald-500/20"
                        : stage.id === "interview"
                        ? "bg-purple-400 ring-2 ring-purple-500/20"
                        : stage.id === "archived"
                        ? "bg-rose-400"
                        : "bg-indigo-400"
                    }`}
                  />
                  <h4 className="text-xs font-bold text-white tracking-tight">
                    {stage.name}
                  </h4>
                </div>
                <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-purple-950 text-purple-300 border border-purple-800/60">
                  {stageCandidates.length}
                </span>
              </div>

              {/* Column Body / Candidate Cards */}
              <div className="p-2.5 flex-1 overflow-y-auto space-y-2.5">
                {stageCandidates.map((c) => {
                  return (
                    <div
                      key={c.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, c.id)}
                      onClick={() => onSelectCandidate(c)}
                      className={`p-3.5 rounded-xl bg-[#1a112c] border border-purple-900/40 hover:border-purple-600/60 transition-all cursor-pointer shadow-md hover:shadow-purple-950/60 group relative ${
                        draggedId === c.id ? "opacity-40" : ""
                      }`}
                    >
                      {/* Top Bar: Role & Ref */}
                      <div className="flex items-center justify-between gap-1 mb-1.5">
                        <span className="text-[9px] font-mono text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded border border-purple-500/20 font-bold">
                          {c.candidate_no}
                        </span>
                        <div className="flex items-center gap-1 text-[10px] font-semibold text-amber-400">
                          <Award className="w-3 h-3" />
                          <span>{c.evaluation_score}</span>
                        </div>
                      </div>

                      {/* Candidate Name & Role */}
                      <div className="space-y-0.5">
                        <h5 className="text-xs font-bold text-white group-hover:text-purple-300 transition-colors truncate">
                          {c.candidate_name}
                        </h5>
                        <p className="text-[11px] font-medium text-purple-200 truncate">
                          {c.target_role}
                        </p>
                      </div>

                      {/* Client Org & Location */}
                      <div className="mt-2 text-[10px] text-slate-400 space-y-1">
                        <div className="flex items-center gap-1.5 truncate">
                          <Building2 className="w-3 h-3 text-purple-400 shrink-0" />
                          <span className="truncate">{c.client_company}</span>
                        </div>
                        <div className="flex items-center gap-1.5 truncate">
                          <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                          <span className="truncate">{c.location}</span>
                        </div>
                      </div>

                      {/* Compensation Pill */}
                      <div className="mt-2.5 pt-2 border-t border-purple-900/30 flex items-center justify-between">
                        <div>
                          <div className="text-[9px] text-slate-500">Package (CTC)</div>
                          <div className="text-xs font-bold text-emerald-400">
                            {sym}{c.expected_ctc.toLocaleString()}
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-[9px] text-slate-500">Practice</div>
                          <div className="text-[10px] font-semibold text-purple-300 truncate max-w-[80px]">
                            {c.practice_id === "c-suite" ? "C-Suite" : c.practice_id === "tech-ai" ? "Tech & AI" : "GTM / Sales"}
                          </div>
                        </div>
                      </div>

                      {/* Stage Specific Badges */}
                      {c.interview_date && c.stage === "Client Interview Round" && (
                        <div className="mt-2 text-[10px] bg-purple-950/80 text-purple-300 px-2 py-1 rounded border border-purple-800/40 flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-purple-400 shrink-0" />
                          <span className="truncate">{c.interview_date}</span>
                        </div>
                      )}

                      {c.invoice_no && c.stage === "Offer Released & Placed (Won)" && (
                        <div className="mt-2 text-[10px] bg-emerald-950/60 text-emerald-300 px-2 py-1 rounded border border-emerald-500/30 flex items-center gap-1 font-mono">
                          <FileText className="w-3 h-3 text-emerald-400 shrink-0" />
                          <span className="truncate">{c.invoice_no}</span>
                        </div>
                      )}

                      {/* Recruiter Footnote */}
                      <div className="mt-2 flex items-center justify-between text-[9px] text-slate-400">
                        <span className="truncate">{c.assigned_recruiter.split(" ")[0]}</span>
                        <ChevronRight className="w-3 h-3 text-slate-500 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  );
                })}

                {stageCandidates.length === 0 && (
                  <div className="h-28 border border-dashed border-purple-900/30 rounded-xl flex items-center justify-center text-[11px] text-slate-400 italic">
                    Drag candidates here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
